/**
 * Quotation List Page
 *
 * Displays all quotations with filtering, searching, and sorting capabilities.
 * Integrates with quotationService for data fetching.
 *
 * @module pages/quotations/QuotationList
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '../../utils/toast';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Collapse,
  Dropdown,
  Modal,
} from 'react-bootstrap';
import { listQuotations, acceptQuotation, markQuotationSent } from '../../services/quotationService';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { formatCurrency, formatDate } from '../../utils/formatters';
// #700 — el total con su rótulo cuando todavía no es un precio.
import QuotationTotal from '../../components/quotations/QuotationTotal';
import { QUOTATION_STATUS } from '../../utils/constants';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import { DataTable } from '../../components/common/DataTable';
import KebabMenu from '../../components/common/KebabMenu';
import './QuotationList.css';

const QuotationList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isTechnician } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();
  const [searchParams] = useSearchParams();

  // Accept quotation modal state
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [acceptPO, setAcceptPO] = useState('');
  const [acceptLoading, setAcceptLoading] = useState(false);

  // Filtro de estado que llega del KPI "Cotizaciones Pendientes" de /dashboard
  // (?status=under_review). Sin esto el numero de la tarjeta y la lista que
  // abre el clic no coincidian (P1-E6). Se valida contra los estados reales:
  // un valor desconocido se ignora en vez de producir una lista vacia.
  const initialStatusParam = Object.values(QUOTATION_STATUS).includes(searchParams.get('status'))
    ? searchParams.get('status')
    : '';

  const [filters, setFilters] = useState({
    status: initialStatusParam,
    search: '',
    dateFrom: '',
    dateTo: '',
    page: 0,
    pageSize: 20,
  });
  const [searchInput, setSearchInput] = useState('');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Ref for debounce timer
  const searchTimeoutRef = useRef(null);

  // Fetch quotations with React Query
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['quotations', filters],
    // Sin `unwrapServiceResult`, el `{data:null, error}` de listQuotations llega
    // a react-query como éxito y el `if (error)` de más abajo (Alert +
    // Reintentar) nunca se ejecuta: la caída se ve como "no hay cotizaciones".
    // Ver utils/serviceResult.js.
    queryFn: () => unwrapServiceResult(listQuotations(filters)),
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new
  });

  /**
   * Handle filter changes
   */
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 0, // Reset to first page
    }));
  };

  /**
   * Handle search input change with debounce (300ms delay)
   */
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: value,
        page: 0,
      }));
    }, 300);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Computed: count active filters (excluding search)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    return count;
  }, [filters.status, filters.dateFrom, filters.dateTo]);

  const hasActiveFilters = activeFiltersCount > 0 || filters.search;

  /**
   * Handle clear all filters
   */
  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      status: '',
      search: '',
      dateFrom: '',
      dateTo: '',
      page: 0,
    }));
    setSearchInput('');
  };

  /**
   * Toggle filters panel
   */
  const toggleFiltersPanel = () => setIsFiltersExpanded((prev) => !prev);

  /**
   * Handle row click
   */
  const handleRowClick = (quotationId) => {
    navigate(`/cotizaciones/${quotationId}`);
  };

  /**
   * Handle create new quotation
   */
  const handleCreateQuotation = () => {
    navigate('/cotizaciones/nueva');
  };

  /**
   * Handle pagination
   */
  const handlePreviousPage = () => {
    if (filters.page > 0) {
      handleFilterChange('page', filters.page - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil((data?.count || 0) / filters.pageSize);
    if (filters.page < totalPages - 1) {
      handleFilterChange('page', filters.page + 1);
    }
  };

  /**
   * Format quotation status
   */
  const formatQuotationStatus = (status) => {
    const statusMap = {
      [QUOTATION_STATUS.DRAFT]: { label: 'Borrador', variant: 'secondary', icon: 'bi-pencil' },
      [QUOTATION_STATUS.UNDER_REVIEW]: { label: 'En Revisión', variant: 'warning', icon: 'bi-hourglass' },
      [QUOTATION_STATUS.APPROVED]: { label: 'Aprobada', variant: 'success', icon: 'bi-check-circle' },
      [QUOTATION_STATUS.REJECTED]: { label: 'Rechazada', variant: 'danger', icon: 'bi-x-circle' },
      [QUOTATION_STATUS.SENT]: { label: 'Enviada', variant: 'info', icon: 'bi-send' },
      // Faltaba: hasta el fix de #A3 aceptar era imposible, así que nunca se
      // vio una fila `accepted` y el badge habría mostrado el texto crudo.
      [QUOTATION_STATUS.ACCEPTED]: { label: 'Aceptada', variant: 'success', icon: 'bi-patch-check' },
    };
    return statusMap[status] || { label: status, variant: 'secondary', icon: 'bi-question-circle' };
  };

  /**
   * Open acceptance modal for a quotation
   */
  const handleOpenAcceptModal = (quotation) => {
    setAcceptTarget(quotation);
    setAcceptPO('');
    setShowAcceptModal(true);
  };

  /**
   * Handle confirm accept quotation
   */
  const handleConfirmAccept = async () => {
    if (!acceptTarget) return;

    try {
      setAcceptLoading(true);
      const result = await acceptQuotation(acceptTarget.id, acceptPO || null);

      if (result.error) throw result.error;

      toast.success('Cotizacion aceptada y proyecto activado exitosamente');
      setShowAcceptModal(false);
      setAcceptTarget(null);
      queryClient.invalidateQueries(['quotations']);
      queryClient.invalidateQueries(['projects']);

      // Navigate to the project
      const projectId = result.data?.project_id || acceptTarget.project_id;
      if (projectId) {
        navigate(`/proyectos/${projectId}`);
      }
    } catch (error) {
      console.error('Accept quotation error:', error);
      toast.error(error.message || 'Error al aceptar la cotizacion');
    } finally {
      setAcceptLoading(false);
    }
  };

  /**
   * Check if a quotation can be accepted.
   *
   * #A3: esto habilitaba también `approved`, y sobre una cotización aprobada
   * la acción FALLABA SIEMPRE. El trigger vivo
   * `validate_quotation_status_transition` (BEFORE UPDATE en `quotations`)
   * sólo admite, desde `approved`: sent | approved | rejected. El
   * `accepted` que escribe el RPC `accept_quotation_and_activate_project`
   * choca de frente con esa lista y sale por excepción:
   * "Invalid quotation status transition from approved to accepted".
   * (Verificado contra prod: 4 aprobadas, 1 enviada, 0 aceptadas — el flujo
   * nunca completó.)
   *
   * La única transición válida hacia `accepted` es desde `sent`. Primero se
   * mira qué permite la base y recién después se decide el botón: ofrecer una
   * acción condenada a fallar es peor que no ofrecerla.
   */
  const canAcceptQuotation = (quotation) => {
    return isAdmin && quotation.status === QUOTATION_STATUS.SENT;
  };

  /**
   * #A3: el paso que le falta a una cotización aprobada para poder aceptarse.
   * Sin esto el camino approved → sent → accepted no existía desde la lista y
   * el usuario quedaba en un callejón sin salida (había que entrar al builder).
   * `markQuotationSent` también mueve el proyecto a `quotation_sent`, y esa
   * escritura sobre `projects` es sólo de admin — de ahí el gate.
   */
  const canMarkSent = (quotation) => {
    return isAdmin && quotation.status === QUOTATION_STATUS.APPROVED;
  };

  /**
   * Handle "Marcar como Enviada" from the list.
   */
  const handleMarkSent = async (quotation) => {
    try {
      const result = await markQuotationSent(quotation.id);
      if (result.error) throw result.error;
      toast.success('Cotización marcada como enviada');
      queryClient.invalidateQueries(['quotations']);
      queryClient.invalidateQueries(['projects']);
    } catch (err) {
      console.error('Mark quotation sent error:', err);
      toast.error(err.message || 'Error al marcar la cotización como enviada');
    }
  };

  // Build table columns — must be before early returns to satisfy Rules of Hooks
  const quotationColumns = useMemo(
    () => [
      {
        id: 'project',
        header: 'Proyecto',
        accessor: (q) => q.project?.project_number || '',
        minWidth: 180,
        cell: (q) => (
          <div>
            <strong>{q.project?.project_number}</strong>
            <div className="small text-muted">{q.project?.title}</div>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Estado',
        accessor: 'status',
        minWidth: 130,
        cell: (q) => {
          const statusInfo = formatQuotationStatus(q.status);
          return (
            <Badge bg={statusInfo.variant}>
              <i className={`bi ${statusInfo.icon} me-1`}></i>
              {statusInfo.label}
            </Badge>
          );
        },
      },
      {
        id: 'subtotal',
        header: 'Subtotal',
        accessor: (q) => q.subtotal || 0,
        align: 'right',
        minWidth: 110,
        cell: (q) => formatCurrency(q.subtotal),
      },
      {
        id: 'tax',
        header: `IVA (${data?.data?.[0]?.tax_rate || 13}%)`,
        accessor: (q) => q.tax_amount || 0,
        align: 'right',
        minWidth: 110,
        cell: (q) => formatCurrency(q.tax_amount),
      },
      {
        id: 'total',
        header: 'Total',
        accessor: (q) => q.total || 0,
        align: 'right',
        minWidth: 110,
        // #700 — SIN `marginUndefined`, y eso es una AUSENCIA DECLARADA, no un
        // olvido. `quotationService.listQuotations` no pide risk_percentage ni
        // profit_percentage: #505 los sacó del select porque esta pantalla la
        // ve un cliente. Sin esos dos campos no se puede medir si al total le
        // falta el margen, y suponerlo pondría la advertencia en TODAS las
        // filas. El rótulo sí aparece en el detalle (QuotationBuilder ya
        // distingue "sin definir" de "a costo" desde #593/#618) y en
        // "Mis cotizaciones", cuyo servicio sí trae los porcentajes.
        cell: (q) => <QuotationTotal quotation={q} bold />,
      },
      {
        id: 'created_by',
        header: 'Creado por',
        accessor: (q) => q.created_by_profile?.full_name || '-',
        minWidth: 150,
      },
      {
        id: 'created_at',
        header: 'Fecha',
        accessor: 'created_at',
        minWidth: 110,
        cell: (q) => formatDate(q.created_at),
      },
      {
        id: 'actions',
        header: '',
        sortable: false,
        resizable: false,
        align: 'center',
        noSelect: true,
        minWidth: 50,
        maxWidth: 60,
        cell: (quotation) => (
          <div data-no-select onClick={(e) => e.stopPropagation()}>
            <KebabMenu label={`Acciones de la cotización de ${quotation.project?.title || quotation.project?.project_number || 'proyecto sin nombre'}`}>
              <Dropdown.Item onClick={() => navigate(`/cotizaciones/${quotation.id}`)}>
                <i className="bi bi-eye me-2"></i>Ver Detalle
              </Dropdown.Item>
              {(isAdmin || isTechnician) && (
                <Dropdown.Item onClick={() => navigate(`/cotizaciones/${quotation.id}/editar`)}>
                  <i className="bi bi-pencil me-2"></i>Editar
                </Dropdown.Item>
              )}
              {canMarkSent(quotation) && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleMarkSent(quotation)}>
                    <i className="bi bi-send me-2 text-info"></i>Marcar como Enviada
                  </Dropdown.Item>
                </>
              )}
              {canAcceptQuotation(quotation) && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleOpenAcceptModal(quotation)}>
                    <i className="bi bi-check-circle me-2 text-success"></i>Aceptar Cotizacion
                  </Dropdown.Item>
                </>
              )}
            </KebabMenu>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, isAdmin, isTechnician, data]
  );

  // Loading state
  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando cotizaciones...</span>
        </Spinner>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error al cargar cotizaciones</Alert.Heading>
          <p>{error.message}</p>
          <Button variant="outline-danger" onClick={() => refetch()}>
            Reintentar
          </Button>
        </Alert>
      </Container>
    );
  }

  const quotations = data?.data || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / filters.pageSize);
  const currentPage = filters.page + 1;

  return (
    <DashboardLayout>
    <Container fluid className="py-4 quotation-list-view">
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center quotation-list-header">
            <div className="mb-3 mb-md-0">
              <h2 className="mb-1">Cotizaciones</h2>
              <p className="text-muted mb-0">{totalCount} cotizaciones totales</p>
            </div>
            <div className="d-flex gap-2 quotation-list-header-actions">
              {(isAdmin || isTechnician) && (
                <Button variant="primary" onClick={handleCreateQuotation}>
                  <i className="bi bi-plus-circle me-2"></i>
                  Nueva Cotización
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Search & Filters */}
      <div className="quotation-search-filter mb-4">
        {/* Global search bar + filter toggle */}
        <div className="quotation-global-search">
          <InputGroup className="flex-grow-1">
            <InputGroup.Text>
              {isFetching && !isLoading ? (
                <Spinner animation="border" size="sm" className="text-muted" />
              ) : (
                <i className="bi bi-search"></i>
              )}
            </InputGroup.Text>
            {/* #A4: el placeholder dice exactamente sobre qué busca
                `listQuotations` (quotation_number). "Buscar cotizaciones..."
                prometía una búsqueda global que no existe. */}
            <Form.Control
              type="text"
              placeholder="Buscar por número de cotización..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label="Buscar por número de cotización"
            />
          </InputGroup>
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          <Button
            variant={activeFiltersCount > 0 ? 'primary' : 'outline-secondary'}
            onClick={toggleFiltersPanel}
            aria-expanded={isFiltersExpanded}
            aria-controls="quotation-advanced-filters"
            className="filter-toggle-btn"
          >
            <i className={`bi bi-chevron-${isFiltersExpanded ? 'up' : 'down'}`}></i>
            <span className="d-none d-md-inline ms-1">Filtros</span>
            {activeFiltersCount > 0 && (
              <Badge bg="light" text="dark" pill className="filter-count-badge">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </div>

        {/* Collapsible advanced filters panel */}
        <Collapse in={isFiltersExpanded}>
          <div id="quotation-advanced-filters">
            <div className="advanced-filters-panel">
              <Row className="g-2 g-md-3">
                <Col xs={12} sm={6} lg={4}>
                  <Form.Label className="small text-muted mb-1">Estado</Form.Label>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    aria-label="Filtrar por estado"
                    size="sm"
                  >
                    <option value="">Todos los estados</option>
                    <option value={QUOTATION_STATUS.DRAFT}>Borrador</option>
                    <option value={QUOTATION_STATUS.UNDER_REVIEW}>En Revision</option>
                    <option value={QUOTATION_STATUS.APPROVED}>Aprobada</option>
                    <option value={QUOTATION_STATUS.REJECTED}>Rechazada</option>
                    <option value={QUOTATION_STATUS.SENT}>Enviada</option>
                    <option value={QUOTATION_STATUS.ACCEPTED}>Aceptada</option>
                  </Form.Select>
                </Col>

                <Col xs={6} sm={6} lg={4}>
                  <Form.Label className="small text-muted mb-1">Desde</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    aria-label="Fecha desde"
                    size="sm"
                  />
                </Col>

                <Col xs={6} sm={6} lg={4}>
                  <Form.Label className="small text-muted mb-1">Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    aria-label="Fecha hasta"
                    size="sm"
                  />
                </Col>
              </Row>

              {/* Clear filters button */}
              {hasActiveFilters && (
                <div className="d-flex justify-content-end mt-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleClearFilters}
                    className="clear-filters-btn"
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Limpiar filtros
                    {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Collapse>
      </div>

      <Row>
        <Col>
          <Card>
            <Card.Body className="p-0">
              {quotations.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-file-earmark-text-x" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                  <h5 className="mt-3 text-muted">No se encontraron cotizaciones</h5>
                  <p className="text-muted">
                    {filters.search || filters.status
                      ? 'Intenta cambiar los filtros'
                      : (isAdmin || isTechnician)
                      ? 'Crea tu primera cotización'
                      : 'No hay cotizaciones disponibles'}
                  </p>
                </div>
              ) : (
                <>
                  {isTableView ? (
                    <DataTable
                      tableId="quotations"
                      data={quotations}
                      rowKey={(q) => q.id}
                      onRowClick={(q) => handleRowClick(q.id)}
                      columns={quotationColumns}
                    />
                  ) : (
                    <Row className="g-3 p-3">
                    {quotations.map((quotation) => {
                      const statusInfo = formatQuotationStatus(quotation.status);
                      return (
                        <Col xs={12} sm={6} lg={4} key={quotation.id}>
                        <Card
                          className={`quotation-card quotation-card-${quotation.status}`}
                          onClick={() => handleRowClick(quotation.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleRowClick(quotation.id);
                            }
                          }}
                        >
                          <Card.Body className="py-3">
                            <div className="quotation-card-header">
                              <div>
                                <div className="quotation-card-project">
                                  {quotation.project?.project_number || 'Sin proyecto'}
                                </div>
                                <div className="quotation-card-project-title">
                                  {quotation.project?.title || '-'}
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <Badge bg={statusInfo.variant}>
                                  <i className={`bi ${statusInfo.icon} me-1`}></i>
                                  {statusInfo.label}
                                </Badge>
                                <KebabMenu label={`Acciones de la cotización de ${quotation.project?.title || quotation.project?.project_number || 'proyecto sin nombre'}`}>
                                  <Dropdown.Item onClick={() => navigate(`/cotizaciones/${quotation.id}`)}>
                                    <i className="bi bi-eye me-2"></i>Ver Detalle
                                  </Dropdown.Item>
                                  {(isAdmin || isTechnician) && (
                                    <Dropdown.Item onClick={() => navigate(`/cotizaciones/${quotation.id}/editar`)}>
                                      <i className="bi bi-pencil me-2"></i>Editar
                                    </Dropdown.Item>
                                  )}
                                  {canMarkSent(quotation) && (
                                    <>
                                      <Dropdown.Divider />
                                      <Dropdown.Item onClick={() => handleMarkSent(quotation)}>
                                        <i className="bi bi-send me-2 text-info"></i>Marcar como Enviada
                                      </Dropdown.Item>
                                    </>
                                  )}
                                  {canAcceptQuotation(quotation) && (
                                    <>
                                      <Dropdown.Divider />
                                      <Dropdown.Item onClick={() => handleOpenAcceptModal(quotation)}>
                                        <i className="bi bi-check-circle me-2 text-success"></i>Aceptar Cotizacion
                                      </Dropdown.Item>
                                    </>
                                  )}
                                </KebabMenu>
                              </div>
                            </div>

                            <div className="quotation-card-body">
                              <div className="quotation-card-row">
                                <span className="quotation-card-label">Subtotal</span>
                                <span className="quotation-card-value">
                                  {formatCurrency(quotation.subtotal)}
                                </span>
                              </div>
                              <div className="quotation-card-row">
                                <span className="quotation-card-label">
                                  IVA ({quotation.tax_rate || 13}%)
                                </span>
                                <span className="quotation-card-value">
                                  {formatCurrency(quotation.tax_amount)}
                                </span>
                              </div>
                              <div className="quotation-card-row">
                                <span className="quotation-card-label">Total</span>
                                <span className="quotation-card-total">
                                  {/* #700 — sin `marginUndefined` a propósito: ver
                                      el comentario en la columna "Total". */}
                                  <QuotationTotal quotation={quotation} />
                                </span>
                              </div>
                            </div>

                            <div className="quotation-card-footer">
                              <span>
                                <i className="bi bi-person me-1"></i>
                                {quotation.created_by_profile?.full_name || '-'}
                              </span>
                              <span>
                                <i className="bi bi-calendar me-1"></i>
                                {formatDate(quotation.created_at)}
                              </span>
                            </div>
                          </Card.Body>
                        </Card>
                        </Col>
                      );
                    })}
                    </Row>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top gap-2">
                      <div className="text-muted small">
                        Pagina {currentPage} de {totalPages}
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handlePreviousPage}
                          disabled={filters.page === 0}
                        >
                          <i className="bi bi-chevron-left"></i> Anterior
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleNextPage}
                          disabled={filters.page >= totalPages - 1}
                        >
                          Siguiente <i className="bi bi-chevron-right"></i>
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Accept Quotation Modal */}
      <Modal show={showAcceptModal} onHide={() => setShowAcceptModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-check-circle me-2 text-success"></i>
            Aceptar Cotizacion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {acceptTarget && (
            <>
              <Alert variant="info">
                <i className="bi bi-info-circle me-2"></i>
                Esta accion activara el proyecto asociado y rechazara otras cotizaciones pendientes del mismo proyecto.
              </Alert>

              <div className="mb-3">
                <strong>Proyecto:</strong> {acceptTarget.project?.project_number} - {acceptTarget.project?.title}
              </div>
              <div className="mb-3">
                <strong>Total:</strong> {formatCurrency(acceptTarget.total)}
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Numero de PO (Opcional)</Form.Label>
                <Form.Control
                  type="text"
                  value={acceptPO}
                  onChange={(e) => setAcceptPO(e.target.value)}
                  placeholder="Ej: PO-2026-001"
                  data-testid="accept-quotation-po-input"
                />
                <Form.Text className="text-muted">
                  Si se proporciona, el proyecto pasara a estado "PO Recibido"
                </Form.Text>
              </Form.Group>

              {/*
                #A4: acá vivía un checkbox "Crear tareas automáticamente a
                partir de los items de la cotización", tildado por defecto.
                No hacía nada: `handleConfirmAccept` llama
                `acceptQuotation(id, po)` sin ese estado, el RPC
                `accept_quotation_and_activate_project` no tiene tal parámetro
                y su cuerpo no menciona tareas en ninguna parte
                (`position('task' in prosrc)` → 0, verificado contra prod).
                Un control que promete trabajo que nadie ejecuta es peor que
                no tenerlo. Si algún día se implementa, el checkbox vuelve
                JUNTO con el parámetro del RPC, no antes.
              */}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAcceptModal(false)} disabled={acceptLoading}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmAccept}
            disabled={acceptLoading}
            data-testid="accept-quotation-confirm-button"
          >
            {acceptLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Procesando...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-1"></i>
                Aceptar Cotizacion
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </DashboardLayout>
  );
};

export default QuotationList;
